import { yupResolver } from '@hookform/resolvers/yup'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import Button from '@mui/material/Button'
import FormControl from '@mui/material/FormControl'
import IconButton from '@mui/material/IconButton'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import axios from 'axios'
import { useSnackbar } from 'notistack'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { addVideo } from '../../../services/videoService'
import type { NewVideo } from '../../../types/Video'

interface NewVideoFormProps {
	fetch: () => void
	onFinished: () => void
}

function NewVideoForm({ fetch, onFinished }: NewVideoFormProps) {
	const { enqueueSnackbar } = useSnackbar()

	const schema = yup.object().shape({
		title: yup.string().required('Título não pode ser vazio'),
		url: yup
			.string()
			.trim()
			.matches(
				/^(https?:\/\/)?(www\.)?youtube\.com\/shorts\/.+$/,
				'Formato de URL incorreto'
			)
			.required('Url não pode ser vazia'),
		thumbnailUrl: yup.string().notRequired(),
		author: yup.string().required('Autor não pode ser vazio'),
		source: yup.string().required('Fonte não pode ser vazia'),
	})

	const {
		control,
		handleSubmit,
		formState: { errors },
	} = useForm<NewVideo>({
		resolver: yupResolver(schema),
	})

	async function submit(data: NewVideo) {
		try {
			await addVideo(data)

			fetch()

			enqueueSnackbar('Video adicionado com sucesso!', {
				variant: 'success',
			})

			onFinished()
		} catch (err) {
			if (axios.isAxiosError(err)) {
				const message = err?.response?.data.message
				enqueueSnackbar(message, { variant: 'error' })
			}
		}
	}

	return (
		<form onSubmit={handleSubmit(submit)}>
			<Stack spacing={2}>
				<Typography variant='h6'>Novo vídeo</Typography>
				<FormControl fullWidth size='small'>
					<InputLabel id='select-souce-label'>Fonte</InputLabel>
					<Controller
						name='source'
						control={control}
						defaultValue='SHORTS'
						render={({ field: { onChange, value } }) => (
							<Select
								labelId='select-souce-label'
								value={value}
								label='Fonte'
								onChange={onChange}
							>
								<MenuItem value='SHORTS'>SHORTS</MenuItem>
							</Select>
						)}
					/>
				</FormControl>
				<Controller
					name='title'
					control={control}
					render={({ field: { onChange } }) => (
						<TextField
							size='small'
							label='Título'
							onChange={onChange}
							error={Boolean(errors?.title)}
							helperText={errors?.title?.message}
						/>
					)}
				/>
				<Stack direction='row'>
					<Controller
						name='url'
						control={control}
						render={({ field: { onChange } }) => (
							<TextField
								fullWidth
								size='small'
								label='Url'
								onChange={onChange}
								error={Boolean(errors?.url)}
								helperText={errors?.url?.message}
							/>
						)}
					/>
					<Tooltip
						title={
							<>
								<Typography>Exemplo:</Typography>
								<em>
									https://www.youtube.com/shorts/video_id
									<br />
								</em>
								<em>
									https://youtube.com/shorts/video_id
									<br />
								</em>
							</>
						}
						placement='right-start'
						sx={{ alignSelf: 'flex-start' }}
					>
						<IconButton>
							<HelpOutlineIcon />
						</IconButton>
					</Tooltip>
				</Stack>
				<Controller
					name='thumbnailUrl'
					control={control}
					render={({ field: { onChange } }) => (
						<TextField
							size='small'
							label='Url da thumbnail (Opcional)'
							onChange={onChange}
							error={Boolean(errors?.thumbnailUrl)}
							helperText={errors?.thumbnailUrl?.message}
						/>
					)}
				/>
				<Controller
					name='author'
					control={control}
					render={({ field: { onChange } }) => (
						<TextField
							size='small'
							label='Autor'
							onChange={onChange}
							error={Boolean(errors?.author)}
							helperText={errors?.author?.message}
						/>
					)}
				/>
				<Button type='submit' variant='contained'>
					Adicionar
				</Button>
			</Stack>
		</form>
	)
}

export default NewVideoForm
